<template>
    <card>
        <template slot="header">
            <div class="idle-summary-heading">
                <h4 class="card-title mb-0">
                    {{ $t("dashboard.idlesummary_title") }}
                </h4>
                <div class="idle-summary-sort" role="group" :aria-label="$t('dashboard.idlesummary_sort_label')">
                    <button
                        v-for="option in sortOptions"
                        :key="option"
                        type="button"
                        :class="['btn', 'btn-sm', 'rounded', 'ml-1', 'idle-sort', sortBy === option ? 'btn-primary' : 'btn-outline-primary']"
                        :aria-pressed="sortBy === option ? 'true' : 'false'"
                        :aria-label="sortLabel(option)"
                        :title="sortLabel(option)"
                        @click="toggleSort(option)"
                    >
                        {{ $t(`dashboard.idlesummary_sort_${option}`) }}
                        <i :class="['fas', 'ml-1', sortBy !== option ? 'fa-sort' : sortDirection === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down']" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
            <p class="card-category text-muted">{{ $t("dashboard.idlesummary_subtitle") }}</p>
        </template>

        <div v-if="status === 'loading'" class="lds-container">
            <div class="lds-ring">
                <div></div><div></div><div></div><div></div>
            </div>
        </div>

        <div v-else-if="status === 'error'" class="text-center py-4">
            <p class="text-danger mb-2">{{ $t("dashboard.idlesummary_error") }}</p>
            <button type="button" class="btn btn-outline-primary btn-sm rounded idle-retry" @click="getData">
                {{ $t("dashboard.idlesummary_retry") }}
            </button>
        </div>

        <div v-else class="row">
            <!-- 75/25: the total up to 30 days (with its three ranges) and the 31 to 365 days total -->
            <div v-for="group in groups" :key="group.period" :class="['mb-3', 'col-12', group.bands.length > 1 ? 'col-lg-9' : 'col-lg-3']">
                <button
                    type="button"
                    :class="['idle-summary-header', group.bands.length === 1 ? `idle-band-${group.bands[0].index}` : 'idle-summary-header-neutral']"
                    :aria-label="$t('dashboard.idlesummary_filter_range', { label: groupLabel(group.range) })"
                    @click="select(group.period)"
                >
                    <span>{{ groupLabel(group.range) }}</span>
                    <strong>{{ group.total }}</strong>
                </button>

                <div class="row">
                    <div v-for="band in group.bands" :key="band.period" :class="group.bands.length > 1 ? 'col-md-4 col-12' : 'col-12'">
                        <button
                            v-if="group.bands.length > 1"
                            type="button"
                            :class="['idle-summary-band', `idle-band-${band.index}`]"
                            :aria-label="$t('dashboard.idlesummary_filter_range', { label: $t('dashboard.idlesummary_period', { min: band.range[0], max: band.range[1] }) })"
                            @click="select(band.period)"
                        >
                            <span>{{ $t("dashboard.idlesummary_period", { min: band.range[0], max: band.range[1] }) }}</span>
                            <strong>{{ band.total }}</strong>
                        </button>

                        <ul :class="['idle-summary-asfs', 'list-unstyled', `idle-band-${band.index}`]">
                            <li v-if="!band.asfs.length" class="text-muted small">{{ $t("dashboard.idlesummary_empty") }}</li>
                            <li v-for="asf in sorted(band.asfs)" :key="asf.id">
                                <button
                                    v-if="asf.id"
                                    type="button"
                                    class="idle-summary-asf idle-summary-asf-action"
                                    :aria-label="$t('dashboard.idlesummary_filter_asf', { name: asf.name, min: band.range[0], max: band.range[1] })"
                                    @click="select(band.period, asf.id)"
                                >
                                    <span>{{ asf.name }}</span>
                                    <strong>{{ asf.total }}</strong>
                                </button>
                                <span v-else class="idle-summary-asf">
                                    <span>{{ $t("dashboard.idlesummary_no_asf") }}</span>
                                    <strong>{{ asf.total }}</strong>
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </card>
</template>

<script>
import $http from "@/helpers/api";
import { MAX_IDLE_DAYS } from "./idlePeriods";

// Day ranges counted per ASF; repairs idle for longer than MAX_IDLE_DAYS are not on the dashboard
const BANDS = [[0, 10], [11, 20], [21, 30], [31, MAX_IDLE_DAYS]];

// Indexes in BANDS shown together under one total
const GROUPS = [[0, 1, 2], [3]];

// Each criterion starts in its natural direction; clicking it again reverses it.
const SORT_OPTIONS = ["total", "name"];
const INITIAL_DIRECTION = { total: "desc", name: "asc" };

function periodParam([min, max]) {
  return `${min},${max}`;
}

// Repairs without an ASF have no name: they stay at the end of the alphabetical list
function byName(a, b) {
  if (!a.name || !b.name) {
    return a.name ? -1 : b.name ? 1 : Number(a.id || 0) - Number(b.id || 0);
  }

  return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });
}

export default {
  name: "RepairsIdleSummary",
  data: () => ({
    sortOptions: SORT_OPTIONS,
    sortBy: SORT_OPTIONS[0],
    sortDirection: INITIAL_DIRECTION.total,
    // "loading" | "success" | "error"; groups are only shown on success, so a failure never reads as zero
    status: "loading",
    summary: [],
    // A newer retry must not be overwritten by an older response.
    latestRequest: 0
  }),
  computed: {
    groups() {
      return GROUPS.map(indexes => {
        const bands = indexes.map(index => ({
          index,
          range: BANDS[index],
          period: periodParam(BANDS[index]),
          total: this.summary[index] ? this.summary[index].total : 0,
          asfs: this.summary[index] ? this.summary[index].asfs : []
        }));
        const range = [bands[0].range[0], bands[bands.length - 1].range[1]];

        return {
          range,
          period: periodParam(range),
          total: bands.reduce((sum, band) => sum + band.total, 0),
          bands
        };
      });
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    select(period, asf) {
      this.$emit("select", asf ? { period, asf, status_id: "" } : { period, status_id: "" });
    },
    sorted(asfs) {
      const direction = this.sortDirection === "asc" ? 1 : -1;

      return [...asfs].sort((a, b) => {
        if (this.sortBy === "name") {
          // Keep unnamed ASFs last in either alphabetical direction.
          if (!a.name || !b.name) {
            return byName(a, b);
          }
          return direction * byName(a, b) || Number(a.id) - Number(b.id);
        }
        return direction * (a.total - b.total) || byName(a, b) || Number(a.id || 0) - Number(b.id || 0);
      });
    },
    toggleSort(option) {
      this.sortDirection = this.sortBy === option
        ? this.sortDirection === "asc" ? "desc" : "asc"
        : INITIAL_DIRECTION[option];
      this.sortBy = option;
    },
    sortLabel(option) {
      const active = this.sortBy === option;
      const next = active ? this.sortDirection === "asc" ? "desc" : "asc" : INITIAL_DIRECTION[option];

      return this.$t("dashboard.idlesummary_sort_accessible", {
        criterion: this.$t(`dashboard.idlesummary_sort_${option}_accessible`),
        current: this.$t(active ? `dashboard.idlesummary_sort_${option}_${this.sortDirection}` : "dashboard.idlesummary_sort_inactive"),
        next: this.$t(`dashboard.idlesummary_sort_${option}_${next}`)
      });
    },
    groupLabel([min, max]) {
      if (min === 0) {
        return this.$t("dashboard.idlesummary_total_upto", { max });
      }

      return this.$t("dashboard.idlesummary_total_between", { min, max });
    },
    getData() {
      const request = ++this.latestRequest;

      this.status = "loading";

      $http
        .get("/stats/repairs/idle_summary", {
          // Independent of the chart and datatable; authorization still scopes the API response.
          params: { periods: BANDS.map(periodParam) }
        })
        .then(resp => {
          if (request !== this.latestRequest) {
            return;
          }

          this.summary = resp.data.data;
          this.status = "success";
        })
        .catch(() => {
          if (request !== this.latestRequest) {
            return;
          }

          this.summary = [];
          this.status = "error";
        });
    }
  }
};
</script>

<style scoped>
/* OpticSans has no face above 500: heavier weights get a synthetic bold on top of the Heavy face */
strong,
.btn.idle-retry,
.btn.idle-sort {
  font-weight: 500;
}

.idle-summary-sort {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
}
.idle-summary-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
}
.idle-summary-sort .btn {
  white-space: nowrap;
}
.btn.idle-sort:focus,
.idle-summary-header:focus,
.idle-summary-band:focus,
.idle-summary-asf-action:focus {
  outline: 2px solid #212529;
  outline-offset: 3px;
}
@media (max-width: 575.98px) {
  .idle-summary-sort {
    flex-wrap: wrap;
    flex-shrink: 1;
    justify-content: flex-start;
  }
}

/* One class per BANDS entry, green (recent) through yellow to red (idle the longest) */
.idle-band-0 {
  --band-color: #28a745;
}
.idle-band-1 {
  --band-color: #ffc107;
  --band-text: #212529;
}
.idle-band-2 {
  --band-color: #ff9800;
}
.idle-band-3 {
  --band-color: #dc3545;
}

.idle-summary-header,
.idle-summary-band {
  width: 100%;
  border: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 4px;
  color: var(--band-text, #fff);
  background-color: var(--band-color);
}
/* The theme paints every <strong> gray: the totals take the header's text color instead */
.idle-summary-header strong,
.idle-summary-band strong {
  color: inherit;
}
.idle-summary-header {
  font-size: 1rem;
}
.idle-summary-header strong {
  font-size: 1.25rem;
}
.idle-summary-header-neutral {
  color: #212529;
  background-color: #e9ecef;
}
.idle-summary-header:hover,
.idle-summary-band:hover {
  filter: brightness(0.92);
}

.idle-summary-asfs {
  max-height: 260px;
  overflow-y: auto;
  border-left: 4px solid var(--band-color);
  padding-left: 0.5rem;
  margin-bottom: 0;
}
.idle-summary-asf {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.15rem 0.25rem;
  color: inherit;
  font-size: 0.875rem;
}
.idle-summary-asf-action {
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
}
.idle-summary-asf-action:hover {
  background-color: #f1f3f5;
}
</style>
